import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Key, Trash2, Loader2, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return str; }
}

export function AdminApiKeys() {
  const { authHeaders } = useAdminAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, hosting_id, hosting_name }
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/api-keys`, { headers: authHeaders() });
      setKeys(data);
    } catch {
      setError('Fehler beim Laden der API-Schlüssel');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/hostings/${deleteTarget.hosting_id}/api-keys/${deleteTarget.id}`,
        { headers: authHeaders() }
      );
      setKeys(prev => prev.filter(k => k.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setError('Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  // Group keys by hosting
  const grouped = keys.reduce((acc, k) => {
    const key = `${k.hosting_id}::${k.hosting_name}`;
    if (!acc[key]) acc[key] = { hosting_id: k.hosting_id, hosting_name: k.hosting_name, keys: [] };
    acc[key].keys.push(k);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
              <Key className="w-6 h-6 text-cyan-400" />
              API-Schlüssel
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Alle API-Schlüssel aller Anbieter – einsehen und verwalten
            </p>
          </div>
          <button onClick={load} disabled={loading} data-testid="refresh-api-keys"
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Key className="w-10 h-10 text-slate-700 mb-3" />
            <p className="text-slate-500 text-sm">Noch keine API-Schlüssel angelegt.</p>
            <p className="text-slate-600 text-xs mt-1">Anbieter können Schlüssel in ihrem Portal erstellen.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.values(grouped).map(group => (
              <div key={group.hosting_id} className="bg-[#0f172a] border border-slate-800 rounded-2xl overflow-hidden" data-testid={`group-${group.hosting_id}`}>
                {/* Provider header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-800 bg-slate-900/30">
                  <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-cyan-400">{group.hosting_name[0].toUpperCase()}</span>
                  </div>
                  <span className="font-semibold text-white text-sm">{group.hosting_name}</span>
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-slate-800 text-slate-500 text-xs border border-slate-700">
                    {group.keys.length} {group.keys.length === 1 ? 'Schlüssel' : 'Schlüssel'}
                  </span>
                </div>

                {/* Keys table */}
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/60">
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vorschau</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Erstellt</th>
                      <th className="px-5 py-2.5 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.keys.map(k => (
                      <tr key={k.id} className="border-b border-slate-800/40 last:border-0 hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3.5">
                          <span className="text-sm font-medium text-slate-200">{k.name || '—'}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <code className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-400">
                            {k.key_preview || '—'}
                          </code>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-slate-500">{formatDate(k.created_at)}</span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <button
                            onClick={() => setDeleteTarget({ ...k, hosting_id: group.hosting_id, hosting_name: group.hosting_name })}
                            data-testid={`delete-key-${k.id}`}
                            className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Schlüssel löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Summary */}
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/30 border border-slate-800 rounded-xl text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-slate-600 flex-shrink-0" />
              Insgesamt <span className="text-slate-400 font-medium mx-1">{keys.length}</span> API-{keys.length === 1 ? 'Schlüssel' : 'Schlüssel'} bei{' '}
              <span className="text-slate-400 font-medium mx-1">{Object.keys(grouped).length}</span>{' '}
              {Object.keys(grouped).length === 1 ? 'Anbieter' : 'Anbietern'}.
              Schlüssel-Inhalte werden aus Sicherheitsgründen nicht angezeigt.
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Schlüssel löschen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Soll der API-Schlüssel{' '}
              <span className="font-semibold text-white">„{deleteTarget?.name}"</span>{' '}
              von <span className="text-cyan-400">{deleteTarget?.hosting_name}</span> unwiderruflich gelöscht werden?
            </p>
            <p className="text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 px-3 py-2 rounded-lg">
              Anwendungen, die diesen Schlüssel nutzen, verlieren sofort den API-Zugriff.
            </p>
            <div className="flex gap-2 pt-1">
              <button onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-key"
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Löschen
              </button>
              <button onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-key"
                className="px-5 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
                Abbrechen
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
